// Component updated to include receipt column and payment history toggle
import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  DollarSign,
  FileText,
  Building2,
  ChevronDown,
  ChevronRight,
  CreditCard,
  AlertTriangle,
  Clock,
  TrendingDown,
  CheckCircle2,
  History,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig,
} from "@/components/ui/chart";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import { ProcesarPagoOCDialog } from "./ProcesarPagoOCDialog";
import { RecepcionDetalleDialog } from "./RecepcionDetalleDialog";
import { AlmasaLoading } from "@/components/brand/AlmasaLoading";

interface OrdenConAdeudo {
  id: string;
  folio: string;
  fecha_orden: string;
  total: number;
  total_ajustado: number | null;
  monto_pagado: number | null;
  status: string;
  status_pago: string;
  tipo_pago: string | null;
  proveedor_id: string | null;
  proveedor_nombre_manual: string | null;
  proveedores: {
    id: string;
    nombre: string;
    telefono: string | null;
    email: string | null;
  } | null;
  entregas: {
    id: string;
    numero_entrega: number;
    status: string;
    recepcion_finalizada_en: string | null;
  }[];
  adeudo: number;
}

interface ProveedorAdeudo {
  proveedorId: string | null;
  proveedorNombre: string;
  totalAdeudo: number;
  ordenes: OrdenConAdeudo[];
  tieneAnticipado: boolean;
}

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(value);
};

const AdeudosProveedoresTab = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mostrarPagadas, setMostrarPagadas] = useState(false);
  const [filtroProveedor, setFiltroProveedor] = useState<string>("todos");
  const [filtroStatusPago, setFiltroStatusPago] = useState<string>("todos");
  const [filtroTipoPago, setFiltroTipoPago] = useState<string>("todos");
  const [expandedProveedores, setExpandedProveedores] = useState<Set<string>>(
    new Set()
  );
  const [selectedOC, setSelectedOC] = useState<OrdenConAdeudo | null>(null);
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [selectedEntregaId, setSelectedEntregaId] = useState<string | null>(null);
  const [showRecepcionDialog, setShowRecepcionDialog] = useState(false);

  // Auto-select OC from URL param ?oc=uuid
  const ocIdFromUrl = searchParams.get("oc");

  // Query principal para obtener OCs con adeudos pendientes o pagadas
  const { data: ordenesConAdeudo = [], isLoading } = useQuery({
    queryKey: ["ordenes-con-adeudo", mostrarPagadas],
    queryFn: async () => {
      let query = supabase
        .from("ordenes_compra")
        .select(
          `
          id, folio, fecha_orden, total, total_ajustado, 
          monto_pagado, status, status_pago, tipo_pago,
          proveedor_id, proveedor_nombre_manual,
          proveedores (id, nombre, telefono, email),
          ordenes_compra_entregas (
            id, numero_entrega, status, recepcion_finalizada_en
          )
        `
        );
      
      if (mostrarPagadas) {
        // Historial: solo pagadas
        query = query.eq("status_pago", "pagado");
      } else {
        // Adeudos pendientes
        query = query
          .in("status_pago", ["pendiente", "parcial"])
          .or('status.in.(recibida,completada,cerrada,parcial),tipo_pago.eq.anticipado');
      }
      
      const { data, error } = await query.order("fecha_orden", { ascending: false });

      if (error) throw error;

      return (data || []).map((oc: any) => ({
        ...oc,
        entregas: oc.ordenes_compra_entregas || [],
        adeudo:
          (oc.total_ajustado || oc.total) - (oc.monto_pagado || 0),
      })) as OrdenConAdeudo[];
    },
    refetchInterval: 30000,
  });

  // Auto-select OC from URL and open payment dialog
  useEffect(() => {
    if (ocIdFromUrl && ordenesConAdeudo.length > 0) {
      const oc = ordenesConAdeudo.find((o) => o.id === ocIdFromUrl);
      if (oc) {
        setSelectedOC(oc);
        setShowPagoDialog(true);
        // Clear the URL param after selecting
        setSearchParams((prev) => {
          prev.delete("oc");
          return prev;
        });
      }
    }
  }, [ocIdFromUrl, ordenesConAdeudo, setSearchParams]);

  // Agrupar por proveedor con filtros aplicados
  const adeudosPorProveedor = useMemo(() => {
    let filtered = ordenesConAdeudo;

    // Aplicar filtros
    if (filtroStatusPago !== "todos") {
      filtered = filtered.filter((oc) => oc.status_pago === filtroStatusPago);
    }
    if (filtroTipoPago !== "todos") {
      filtered = filtered.filter((oc) => oc.tipo_pago === filtroTipoPago);
    }

    const map = new Map<string, ProveedorAdeudo>();

    filtered.forEach((oc) => {
      const nombre =
        oc.proveedores?.nombre || oc.proveedor_nombre_manual || "Sin proveedor";

      if (!map.has(nombre)) {
        map.set(nombre, {
          proveedorId: oc.proveedor_id,
          proveedorNombre: nombre,
          totalAdeudo: 0,
          ordenes: [],
          tieneAnticipado: false,
        });
      }

      const entry = map.get(nombre)!;
      entry.totalAdeudo += oc.adeudo;
      entry.ordenes.push(oc);
      if (oc.tipo_pago === "anticipado") entry.tieneAnticipado = true;
    });

    let result = Array.from(map.values()).sort(
      (a, b) => b.totalAdeudo - a.totalAdeudo
    );

    // Filtrar por proveedor específico
    if (filtroProveedor !== "todos") {
      result = result.filter((p) => p.proveedorNombre === filtroProveedor);
    }

    return result;
  }, [ordenesConAdeudo, filtroProveedor, filtroStatusPago, filtroTipoPago]);

  // Calcular KPIs - dinámico según el toggle
  const kpis = useMemo(() => {
    if (mostrarPagadas) {
      // Historial de pagadas
      const totalPagado = adeudosPorProveedor.reduce(
        (sum, p) => sum + p.ordenes.reduce((s, o) => s + (o.monto_pagado || 0), 0),
        0
      );
      const totalOCsPagadas = adeudosPorProveedor.reduce(
        (sum, p) => sum + p.ordenes.length,
        0
      );
      const proveedoresPagados = adeudosPorProveedor.length;

      return {
        total: totalPagado,
        totalOCs: totalOCsPagadas,
        proveedores: proveedoresPagados,
        ocsAnticipadas: 0,
      };
    } else {
      // Adeudos pendientes
      const totalAdeudado = adeudosPorProveedor.reduce(
        (sum, p) => sum + p.totalAdeudo,
        0
      );
      const totalOCsPendientes = adeudosPorProveedor.reduce(
        (sum, p) => sum + p.ordenes.length,
        0
      );
      const proveedoresConAdeudo = adeudosPorProveedor.length;
      const ocsAnticipadas = adeudosPorProveedor.reduce(
        (sum, p) => sum + p.ordenes.filter((o) => o.tipo_pago === "anticipado").length,
        0
      );

      return {
        total: totalAdeudado,
        totalOCs: totalOCsPendientes,
        proveedores: proveedoresConAdeudo,
        ocsAnticipadas,
      };
    }
  }, [adeudosPorProveedor, mostrarPagadas]);

  // Lista única de proveedores para el filtro
  const proveedoresUnicos = useMemo(() => {
    const nombres = new Set<string>();
    ordenesConAdeudo.forEach((oc) => {
      const nombre =
        oc.proveedores?.nombre || oc.proveedor_nombre_manual || "Sin proveedor";
      nombres.add(nombre);
    });
    return Array.from(nombres).sort();
  }, [ordenesConAdeudo]);

  // Datos para el gráfico pie
  const chartData = useMemo(() => {
    return adeudosPorProveedor.slice(0, 5).map((p, index) => ({
      name: p.proveedorNombre.length > 20 
        ? p.proveedorNombre.substring(0, 20) + "..." 
        : p.proveedorNombre,
      value: p.totalAdeudo,
      fill: CHART_COLORS[index % CHART_COLORS.length],
    }));
  }, [adeudosPorProveedor]);

  const chartConfig: ChartConfig = useMemo(() => {
    const config: ChartConfig = {};
    chartData.forEach((item, index) => {
      config[item.name] = {
        label: item.name,
        color: CHART_COLORS[index % CHART_COLORS.length],
      };
    });
    return config;
  }, [chartData]);

  const toggleProveedor = (nombre: string) => {
    setExpandedProveedores((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nombre)) {
        newSet.delete(nombre);
      } else {
        newSet.add(nombre);
      }
      return newSet;
    });
  };

  const handleProcesarPago = (orden: OrdenConAdeudo) => {
    setSelectedOC(orden);
    setShowPagoDialog(true);
  };

  const getStatusPagoBadge = (status: string) => {
    switch (status) {
      case "pendiente":
        return <Badge variant="destructive">Pendiente</Badge>;
      case "parcial":
        return <Badge className="bg-amber-500 text-white">Parcial</Badge>;
      case "pagado":
        return <Badge className="bg-green-600 text-white">Pagado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusOCBadge = (status: string) => {
    switch (status) {
      case "borrador":
        return <Badge variant="secondary">Borrador</Badge>;
      case "pendiente":
        return <Badge variant="outline">Pendiente</Badge>;
      case "autorizada":
        return <Badge className="bg-blue-500 text-white">Autorizada</Badge>;
      case "enviada":
        return <Badge className="bg-purple-500 text-white">Enviada</Badge>;
      case "recibida":
        return <Badge className="bg-green-600 text-white">Recibida</Badge>;
      case "cerrada":
        return <Badge className="bg-gray-500 text-white">Cerrada</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <AlmasaLoading size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con Toggle */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            {mostrarPagadas ? (
              <>
                <History className="h-5 w-5 text-green-600" />
                Historial de Pagos a Proveedores
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 text-amber-600" />
                Adeudos a Proveedores
              </>
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            {mostrarPagadas 
              ? "Órdenes de compra con pago completado"
              : "Gestión de pagos pendientes y cuentas por pagar"}
          </p>
        </div>
        
        <div className="flex items-center gap-3 bg-muted/50 rounded-lg p-3">
          <Label htmlFor="toggle-pagadas" className="text-sm cursor-pointer">
            Pendientes
          </Label>
          <Switch 
            id="toggle-pagadas"
            checked={mostrarPagadas} 
            onCheckedChange={setMostrarPagadas} 
          />
          <Label htmlFor="toggle-pagadas" className="text-sm cursor-pointer flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Pagadas
          </Label>
        </div>
      </div>

      {/* KPI Cards - Dinámicos según toggle */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {mostrarPagadas ? "Total Pagado" : "Total Adeudado"}
            </CardTitle>
            <DollarSign className={`h-4 w-4 ${mostrarPagadas ? 'text-green-600' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${mostrarPagadas ? 'text-green-600' : 'text-destructive'}`}>
              {formatCurrency(kpis.total)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {mostrarPagadas 
                ? "Suma de todos los pagos realizados"
                : "Suma de todos los adeudos pendientes"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {mostrarPagadas ? "OCs Pagadas" : "OCs Pendientes"}
            </CardTitle>
            <FileText className={`h-4 w-4 ${mostrarPagadas ? 'text-green-600' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.totalOCs}</div>
            {!mostrarPagadas && kpis.ocsAnticipadas > 0 && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {kpis.ocsAnticipadas} con pago anticipado
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {mostrarPagadas ? "Proveedores Pagados" : "Proveedores con Adeudo"}
            </CardTitle>
            <Building2 className={`h-4 w-4 ${mostrarPagadas ? 'text-green-600' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.proveedores}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Proveedores únicos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-1 block">Proveedor</label>
              <Select value={filtroProveedor} onValueChange={setFiltroProveedor}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los proveedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los proveedores</SelectItem>
                  {proveedoresUnicos.map((nombre) => (
                    <SelectItem key={nombre} value={nombre}>
                      {nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[180px]">
              <label className="text-sm font-medium mb-1 block">Status Pago</label>
              <Select value={filtroStatusPago} onValueChange={setFiltroStatusPago}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="parcial">Parcial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-[180px]">
              <label className="text-sm font-medium mb-1 block">Tipo de Pago</label>
              <Select value={filtroTipoPago} onValueChange={setFiltroTipoPago}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="contra_entrega">Contra Entrega</SelectItem>
                  <SelectItem value="anticipado">Anticipado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contenido principal: Cards y Gráfico */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cards por Proveedor */}
        <div className="lg:col-span-2 space-y-4">
          {adeudosPorProveedor.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                {mostrarPagadas ? (
                  <>
                    <History className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <h3 className="text-lg font-medium">Sin historial de pagos</h3>
                    <p className="text-sm text-muted-foreground">
                      No hay órdenes de compra pagadas registradas
                    </p>
                  </>
                ) : (
                  <>
                    <TrendingDown className="h-12 w-12 mx-auto text-green-500 mb-3" />
                    <h3 className="text-lg font-medium">Sin adeudos pendientes</h3>
                    <p className="text-sm text-muted-foreground">
                      No hay órdenes de compra con pagos pendientes
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            adeudosPorProveedor.map((proveedor) => (
              <Card key={proveedor.proveedorNombre}>
                <Collapsible
                  open={expandedProveedores.has(proveedor.proveedorNombre)}
                  onOpenChange={() => toggleProveedor(proveedor.proveedorNombre)}
                >
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="flex flex-row items-center justify-between py-4 cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        {expandedProveedores.has(proveedor.proveedorNombre) ? (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        )}
                        <div className="text-left">
                          <CardTitle className="text-base flex items-center gap-2">
                            {proveedor.proveedorNombre}
                            {proveedor.tieneAnticipado && (
                              <Badge
                                variant="outline"
                                className="text-amber-600 border-amber-500"
                              >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Anticipo req.
                              </Badge>
                            )}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {proveedor.ordenes.length} OC
                            {proveedor.ordenes.length > 1 ? "s" : ""} {mostrarPagadas ? "pagada" : "pendiente"}
                            {proveedor.ordenes.length > 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${mostrarPagadas ? 'text-green-600' : 'text-destructive'}`}>
                          {mostrarPagadas ? 'Pagado: ' : 'Adeudo: '}
                          {formatCurrency(mostrarPagadas 
                            ? proveedor.ordenes.reduce((s, o) => s + (o.monto_pagado || 0), 0)
                            : proveedor.totalAdeudo
                          )}
                        </p>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <Table className="table-fixed w-full">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-14">Folio</TableHead>
                            <TableHead className="w-16">Fecha</TableHead>
                            <TableHead className="w-20">Status</TableHead>
                            <TableHead className="w-20">Pago</TableHead>
                            <TableHead className="w-20 text-right">Total</TableHead>
                            <TableHead className="w-20 text-right">Pagado</TableHead>
                            
                            <TableHead className="w-16">Recep.</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {proveedor.ordenes.map((orden) => (
                            <TableRow key={orden.id}>
                              <TableCell className="font-medium text-xs">
                                #{orden.folio.split('-').pop()}
                              </TableCell>
                              <TableCell className="text-xs">
                                {format(new Date(orden.fecha_orden), "dd/MM", {
                                  locale: es,
                                })}
                              </TableCell>
                              <TableCell>{getStatusOCBadge(orden.status)}</TableCell>
                              <TableCell>
                                {getStatusPagoBadge(orden.status_pago)}
                                {orden.tipo_pago === "anticipado" && (
                                  <Badge
                                    variant="outline"
                                    className="ml-1 text-[10px] px-1 border-amber-500 text-amber-600"
                                  >
                                    Ant
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right text-xs">
                                {formatCurrency(orden.total_ajustado || orden.total)}
                              </TableCell>
                              <TableCell className="text-right text-xs">
                                {formatCurrency(orden.monto_pagado || 0)}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const recibidas = orden.entregas?.filter(e => e.status === 'recibida') || [];
                                  
                                  if (recibidas.length === 0) {
                                    return orden.tipo_pago === 'anticipado' 
                                      ? <span className="text-xs text-muted-foreground">N/A</span>
                                      : <span className="text-xs text-muted-foreground">-</span>;
                                  }
                                  
                                  if (recibidas.length === 1) {
                                    return (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-primary h-7 px-2 text-xs"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedEntregaId(recibidas[0].id);
                                          setShowRecepcionDialog(true);
                                        }}
                                      >
                                        <FileText className="h-3 w-3 mr-1" />
                                        Ver
                                      </Button>
                                    );
                                  }
                                  
                                  return (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button size="sm" variant="ghost" className="text-primary h-7 px-2 text-xs">
                                          <FileText className="h-3 w-3 mr-1" />
                                          {recibidas.length}
                                          <ChevronDown className="h-3 w-3 ml-0.5" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-48 p-2" align="start">
                                        <div className="space-y-1">
                                          {recibidas.map((entrega) => (
                                            <Button
                                              key={entrega.id}
                                              size="sm"
                                              variant="ghost"
                                              className="w-full justify-start text-xs h-7"
                                              onClick={() => {
                                                setSelectedEntregaId(entrega.id);
                                                setShowRecepcionDialog(true);
                                              }}
                                            >
                                              <FileText className="h-3 w-3 mr-2" />
                                              #{entrega.numero_entrega}
                                              {entrega.recepcion_finalizada_en && (
                                                <span className="ml-auto text-muted-foreground">
                                                  {format(new Date(entrega.recepcion_finalizada_en), 'dd/MM')}
                                                </span>
                                              )}
                                            </Button>
                                          ))}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                {mostrarPagadas ? (
                                  <Badge className="bg-green-600 text-white text-xs">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Pagado
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleProcesarPago(orden);
                                    }}
                                  >
                                    <CreditCard className="h-3 w-3 mr-1" />
                                    Pagar
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))
          )}
        </div>

        {/* Gráfico Pie */}
        <div>
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-base">Distribución por Proveedor</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) =>
                        `${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value) =>
                            formatCurrency(value as number)
                          }
                        />
                      }
                    />
                    <Legend />
                  </PieChart>
                </ChartContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Sin datos para mostrar
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog de Procesar Pago */}
      {selectedOC && (
        <ProcesarPagoOCDialog
          open={showPagoDialog}
          onOpenChange={(open) => {
            setShowPagoDialog(open);
            if (!open) {
              setSelectedOC(null);
              queryClient.invalidateQueries({ queryKey: ["ordenes-con-adeudo"] });
            }
          }}
          orden={{
            id: selectedOC.id,
            folio: selectedOC.folio,
            proveedor_id: selectedOC.proveedor_id,
            proveedor_nombre: selectedOC.proveedores?.nombre || selectedOC.proveedor_nombre_manual || "Sin proveedor",
            total: selectedOC.total,
            total_ajustado: selectedOC.total_ajustado,
            monto_pagado: selectedOC.monto_pagado,
            monto_devoluciones: 0,
            tipo_pago: selectedOC.tipo_pago,
          }}
          onOpenFacturas={() => {}}
        />
      )}

      {/* Dialog de Detalle de Recepción */}
      <RecepcionDetalleDialog
        entregaId={selectedEntregaId}
        open={showRecepcionDialog}
        onOpenChange={(open) => {
          setShowRecepcionDialog(open);
          if (!open) setSelectedEntregaId(null);
        }}
      />
    </div>
  );
};

export default AdeudosProveedoresTab;
